var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { action, computed, makeObservable, observable, when } from 'mobx';
import { getImageInfo } from '../../algolib';
import { AssetErrorMessage, AssetStatusType, } from './types';
import { generateImagePreview, shouldGenerateImagePreview } from '../../artworkProcessing/imageClassification';
import { fetchAssetIdFromSherbertUrl, ImageFormat, isAllowedFormat, isDocumentItem, isJsonFile, isJsonUrl, isRaster, isRoute, Route, VariantTypes, } from '../../artworkProcessing/utility';
import { createAssetByFileWithPolling, createAssetByUriWithPolling, deleteAsset, getVariantMetadataWithPolling, hideAsset, patchAsset, presignAsset, unhideAsset, getPageImageInfo, getVariantWithPolling, getMultiPageVariant, getDocumentItemInfoV2, createAssetByFileWithPollingV3, createAssetByUriWithPollingV3, } from '../../client';
import { DEFAULT_SHERBERT_BASE_URL, UPLOADS_BASE_URL } from '../../client/baseUrl';
import { isAbortError, naiveRandomId } from '../../utilities';
import { isAssetFromData, isDerivedAsset, isFileAsset, isUriAsset } from '../typeGuards';
import { assetStoreEvent, AssetStoreEvent } from '../assetStoreEvent';
import { getQueryParams } from '../../artworkProcessing/common';
import { getMultiPageVariantV3, getVariantMetadataWithPollingV3, getVariantWithPollingV3, getAssetInfoV3, getImageClassificationInfoV3, getStorageInfoV3, getImageInfoMultipageV3, getImageInfoV3WithPolling, getExtractedLogoV3, getFontsV3, getColorsInfoV3, getDocumentItemInfoV3, createAssetByDocumentItemWithPollingV3, getImageTextV3, getVectorExtractedLogo, getVectorExtractedLogoWithVersionV3, getVectorExtractedLogoWithVersionAndItemNumberV3, } from '../../client/sherbertClientV3';
import { getPDFInfo } from '../../artworkProcessing';
export class Asset {
    constructor(options) {
        var _a, _b, _c;
        /**
         * An  identifier which can be used to distinguish between instances of the `Asset` class. Helpful when {@link Asset.data} is not yet available.
         *
         * @example
         * ```jsx
         * <ul>
         *  {assets.map(asset =>
         *      <li key={asset.instanceId}>...</li>
         *  )}
         * </ul>
         * ```
         */
        Object.defineProperty(this, "instanceId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: naiveRandomId()
        });
        Object.defineProperty(this, "composeOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "authProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "uploadOptions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "preparePromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "variantCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_pageImageInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "assetStoreEventTarget", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { type: AssetStatusType.Unprepared }
        });
        Object.defineProperty(this, "_data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_signature", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getCroppingParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (left, right, top, bottom) => {
                const isInvalid = [left, right, top, bottom].some((param) => param === null || param === undefined || param < 0 || param >= 1);
                if (isInvalid) {
                    throw new Error('Unsupported args found for cropped');
                }
                if (left && right && left + right >= 1) {
                    throw new Error('Sum of left and right crop values must be less than 1');
                }
                if (top && bottom && top + bottom >= 1) {
                    throw new Error('Sum of top and bottom crop values must be less than 1');
                }
                return `:m=fractions,b=${bottom},l=${left},r=${right},t=${top}`;
            }
        });
        Object.defineProperty(this, "getMonochromeColorParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (threshold, color, inverted) => {
                let path = '';
                if (threshold && (threshold > 1 || threshold < 0)) {
                    throw new Error('threshold value must be in between 0 and 1');
                }
                if (threshold) {
                    path += `:t=${threshold}`;
                }
                if (color) {
                    path += threshold ? `,c=${color}` : `:c=${color}`;
                }
                if (inverted !== undefined && inverted !== null) {
                    path += threshold || color ? `,i=${inverted}` : `:i=${inverted}`;
                }
                return path;
            }
        });
        makeObservable(this);
        this.authProvider = options.authProvider;
        this.variantCache = new Map();
        this.assetStoreEventTarget = options.assetStoreEventTarget;
        if (isDerivedAsset(options)) {
            this.composeOptions = Object.assign({}, options);
        }
        else if (isAssetFromData(options)) {
            const { assetData, signature } = options;
            this.setAssetData(assetData);
            if ((_a = assetData.info.asset) === null || _a === void 0 ? void 0 : _a.hidden) {
                this.setStatus({ type: AssetStatusType.Hidden, isHidden: true });
            }
            else {
                this.setStatus({ type: AssetStatusType.Prepared });
            }
            this.setSignature(signature);
        }
        else {
            this.uploadOptions = options;
            if ((_c = (_b = options.info) === null || _b === void 0 ? void 0 : _b.asset) === null || _c === void 0 ? void 0 : _c.hidden) {
                this.setStatus({ type: AssetStatusType.Hidden, isHidden: true });
            }
        }
        when(() => this.status.type === AssetStatusType.Prepared, () => __awaiter(this, void 0, void 0, function* () {
            var _d, _e, _f, _g, _h, _j, _k;
            if (!this.isPrepared() || this.data.type !== 'image')
                return;
            if (((_d = this.data.info.asset) === null || _d === void 0 ? void 0 : _d.sourceUri) && this.data.info.image)
                return;
            try {
                if (this.data.isLocallyProcessed || ((_e = this.data.info.asset) === null || _e === void 0 ? void 0 : _e.sourceUri)) {
                    // If the asset is a variant, do not generate Image Info
                    if ((_h = (_g = (_f = this._data) === null || _f === void 0 ? void 0 : _f.info) === null || _g === void 0 ? void 0 : _g.asset) === null || _h === void 0 ? void 0 : _h.isVariant) {
                        return;
                    }
                    const { data: imageInfo } = yield getImageInfoV3WithPolling({ path: { id: this.data.id } }, { headers: yield this.getAuthHeaders() });
                    const updatedAssetData = Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { image: imageInfo }), isLocallyProcessed: false });
                    this.setAssetData(updatedAssetData);
                    const assetImageInfo = {
                        id: this.data.id,
                        imageInfo: imageInfo,
                    };
                    (_j = this.assetStoreEventTarget) === null || _j === void 0 ? void 0 : _j.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoServerSuccess, { detail: assetImageInfo }));
                }
                // Don't set image info if the request fails, but don't throw an error either
                // eslint-disable-next-line no-empty
            }
            catch (error) {
                (_k = this.assetStoreEventTarget) === null || _k === void 0 ? void 0 : _k.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoServerFailure, { detail: error.data }));
            }
        }));
    }
    get data() {
        return this._data;
    }
    // Derived variants
    get original() {
        return this.derive('original');
    }
    get print() {
        return this.derive('print');
    }
    get webPreview() {
        return this.derive('webPreview');
    }
    get thumbnail() {
        return this.derive('thumb');
    }
    get embroidery() {
        return this.derive('embroidery');
    }
    get monochrome() {
        return this.derive('monochrome');
    }
    get sharpened() {
        return this.derive('sharpened');
    }
    get backgroundRemoved() {
        return this.derive('backgroundRemoved');
    }
    get embroiderify() {
        return this.derive('embroiderify');
    }
    get invertColor() {
        return this.derive('invertColor');
    }
    /**
     * cerate variant and return metadata of the asset.
     */
    getMetadata(assetData, variant, route) {
        return __awaiter(this, void 0, void 0, function* () {
            let variantAssetData;
            try {
                switch (route || Route.V3) {
                    case Route.V2:
                        variantAssetData = (yield getVariantMetadataWithPolling({ path: { id: assetData.id, variant } }, { headers: yield this.getAuthHeaders() })).data;
                        break;
                    case Route.V3:
                        variantAssetData = (yield getVariantMetadataWithPollingV3({ path: { id: assetData.id, variant } }, { headers: yield this.getAuthHeaders() })).data;
                        break;
                    default:
                        throw new Error(`${route} is not a valid route`);
                }
            }
            catch (error) {
                throw new Error(error);
            }
            return variantAssetData;
        });
    }
    /**
     * Return print variant of the asset.
     */
    getPrint({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Print, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return webPreview variant of the asset.
     */
    getWebPreview({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.WebPreview, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return Thumb variant of the asset.
     */
    getThumb({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Thumb, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return Sharpened variant of the asset.
     */
    getSharpened({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Sharpened, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return background removed variant of the asset.
     */
    getBackgroundRemoved({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.BackgroundRemoved, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return monochrome removed variant of the asset.
     */
    getMonochrome({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Monochrome, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return Embroidery variant of the asset.
     */
    getEmbroidery({ route = Route.V3 } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Embroidery, route);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Return Embroiderify variant of the asset.
     */
    getEmbroiderify() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (!this._data) {
                    throw new Error('cannot find data parent asset');
                }
                const data = yield this.getMetadata(this._data, VariantTypes.Embroiderify, Route.V3);
                return new Asset({ assetData: data, authProvider: this.authProvider });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Fetch Image info if not present and return Asset with imageInfo.
     */
    fetchImageInfo() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.data) {
                    if ((_a = this.data.info) === null || _a === void 0 ? void 0 : _a.image) {
                        // the imageInfo for the asset is already present.
                        return this;
                    }
                    const { data: imageInfo } = yield getImageInfoV3WithPolling({ path: { id: this.data.id } }, { headers: yield this.getAuthHeaders() });
                    const updatedAssetData = Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { image: imageInfo }), isLocallyProcessed: false });
                    this.setAssetData(updatedAssetData);
                    const assetImageInfo = {
                        id: this.data.id,
                        imageInfo: imageInfo,
                    };
                    (_b = this.assetStoreEventTarget) === null || _b === void 0 ? void 0 : _b.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoServerSuccess, { detail: assetImageInfo }));
                }
                return this;
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * returns variant based on variant type.
     * @param {getVariantParams} params Object holding getVariant parameters for this method call.
     * @returns {Promise<void>} Returns void Promise.Throws an error on getVariant failure .
     */
    getVariant(param) {
        return __awaiter(this, void 0, void 0, function* () {
            const { variant, route } = param;
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find asset');
                }
                switch (route || Route.V3) {
                    case Route.V2:
                        yield getVariantWithPolling({ path: { id: this._data.id, variant } }, { headers: yield this.getAuthHeaders() });
                        break;
                    case Route.V3:
                        yield getVariantWithPollingV3({ path: { id: this._data.id, variant } }, { headers: yield this.getAuthHeaders() });
                        break;
                    default:
                        throw new Error(`${route} is not a valid route`);
                }
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * returns page wise variant based on variant type and page number.
     * @param {getMultiPageVariantParams} params Object holding getMultiPageVariantParams parameters for this method call.
     * @returns {Promise<void>} Returns void Promise.Throws an error on  getMultiPageVariant failure.
     */
    getMultiPageVariant(param) {
        return __awaiter(this, void 0, void 0, function* () {
            const { variant, route, pageNum } = param;
            if (route !== null && route !== undefined && !isRoute(route)) {
                throw new Error(`${route} is not a valid route`);
            }
            if (pageNum !== null &&
                pageNum !== undefined &&
                variant !== VariantTypes.WebPreview &&
                variant !== VariantTypes.Thumb) {
                throw new Error(`MultiPage variant generation is not applicable for: ${variant}`);
            }
            try {
                if (!this._data) {
                    throw new Error('cannot find asset');
                }
                const pageNumber = `page_${pageNum}`;
                switch (route || Route.V3) {
                    case Route.V2:
                        yield getMultiPageVariant({ path: { id: this._data.id, variant, pageNum } }, { headers: yield this.getAuthHeaders() });
                        break;
                    case Route.V3:
                        yield getMultiPageVariantV3({ path: { id: this._data.id, variant, pageNum: pageNumber } }, { headers: yield this.getAuthHeaders() });
                        break;
                    default:
                        throw new Error(`${route} is not a valid route`);
                }
            }
            catch (error) {
                throw new Error(`unable to get multi page variant ${error.message}`);
            }
        });
    }
    // eslint-disable-next-line class-methods-use-this, consistent-return
    getClientSideAssetInfo({ file }) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (file.type.includes(ImageFormat.Pdf)) {
                    return getPDFInfo(file);
                }
                if (isRaster(file)) {
                    return getImageInfo(file);
                }
            }
            catch (error) {
                // consume the error
            }
        });
    }
    /**
     * Gets the URL for the asset of the appropriate AssetType value.
     * @param {GetMultiPageUrlParams} ParametersObject used to pass parameters into this function. See type documentation for more detailed information.
     * This indicates whether or not to include the signature from the original so that presign doesn't
     * need to be called separately.
     * @returns {string} Returns the requested asset URL.
     */
    getMultiPageUrl({ includeSignature = true, showDeleted = false, pageNum }) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const assetId = (_b = (_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.parentAsset._data) === null || _b === void 0 ? void 0 : _b.id;
        const url = new URL(`${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${assetId}`);
        if (includeSignature) {
            url.searchParams.set('signature', this.signature ? this.signature : '');
        }
        let pathName = url.pathname;
        if (((_e = (_d = (_c = this.composeOptions) === null || _c === void 0 ? void 0 : _c.parentAsset._data) === null || _d === void 0 ? void 0 : _d.info.image) === null || _e === void 0 ? void 0 : _e.format) &&
            isAllowedFormat((_h = (_g = (_f = this.composeOptions) === null || _f === void 0 ? void 0 : _f.parentAsset._data) === null || _g === void 0 ? void 0 : _g.info.image) === null || _h === void 0 ? void 0 : _h.format) &&
            pageNum !== undefined &&
            'numPages' in this.composeOptions.parentAsset._data.info.image &&
            this.composeOptions.parentAsset._data.info.image.numPages) {
            if (pageNum >= 1 && pageNum <= this.composeOptions.parentAsset._data.info.image.numPages) {
                pathName += `/page_${pageNum}`;
            }
            else {
                throw new Error(`page number must be in range: [1-${this.composeOptions.parentAsset._data.info.image.numPages}]`);
            }
        }
        pathName += `/${(_j = this.composeOptions) === null || _j === void 0 ? void 0 : _j.processTypeUrlPath}`;
        url.pathname = pathName;
        const { searchParams } = url;
        const signatureValue = searchParams.get('signature');
        if (!signatureValue) {
            url.searchParams.delete('signature');
        }
        if (searchParams.has('showDeleted')) {
            searchParams.set('showDeleted', showDeleted.toString());
        }
        else {
            searchParams.append('showDeleted', showDeleted.toString());
        }
        return url.toString();
    }
    /**
     * Gets the URL for the asset of the appropriate AssetType value, for example Print, Preview, or Original variants.
     * @param {GetUrlParams} ParametersObject used to pass parameters into this function. See type documentation for more detailed information.
     * This indicates whether or not to include the signature from the original so that presign doesn't
     * need to be called separately.
     * @returns {string} Returns the requested asset URL.
     */
    getUrl({ includeSignature = true, showDeleted = false, pageNum } = {}) {
        var _a, _b;
        switch ((_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.processTypeUrlPath) {
            case VariantTypes.Original:
                return this.getVariantPath(VariantTypes.Original, includeSignature, showDeleted, pageNum);
            case VariantTypes.Thumb:
                return this.getVariantPath(VariantTypes.Thumb, includeSignature, showDeleted, pageNum);
            case VariantTypes.Print:
                return this.getVariantPath(VariantTypes.Print, includeSignature, showDeleted, pageNum);
            case VariantTypes.WebPreview:
                return this.getVariantPath(VariantTypes.WebPreview, includeSignature, showDeleted, pageNum);
            case VariantTypes.Embroidery:
                return this.getVariantPath(VariantTypes.Embroidery, includeSignature, showDeleted, pageNum);
            case VariantTypes.BackgroundRemoved:
                return this.getVariantPath(VariantTypes.BackgroundRemoved, includeSignature, showDeleted, pageNum);
            case VariantTypes.Sharpened:
                return this.getVariantPath(VariantTypes.Sharpened, includeSignature, showDeleted, pageNum);
            case VariantTypes.Monochrome:
                return this.getVariantPath(VariantTypes.Monochrome, includeSignature, showDeleted, pageNum);
            case VariantTypes.Embroiderify:
                return this.getVariantPath(VariantTypes.Embroiderify, includeSignature, showDeleted, pageNum);
            case VariantTypes.InvertColor:
                return this.getVariantPath(VariantTypes.InvertColor, includeSignature, showDeleted, pageNum);
            default:
                throw Error(`Unknown asset type --> ${(_b = this.composeOptions) === null || _b === void 0 ? void 0 : _b.processTypeUrlPath}`);
        }
    }
    getVariantPath(variantType, includeSignature, showDeleted, pageNum) {
        var _a, _b, _c, _d, _e, _f;
        const url = new URL(this.getUrlInternal());
        if (includeSignature) {
            url.searchParams.set('signature', this.signature ? this.signature : '');
        }
        if (pageNum !== null &&
            pageNum !== undefined &&
            variantType !== VariantTypes.WebPreview &&
            variantType !== VariantTypes.Thumb &&
            variantType !== VariantTypes.Embroiderify &&
            variantType !== VariantTypes.InvertColor) {
            throw new Error(`MultiPage variant generation is not applicable for: ${variantType}`);
        }
        let pathName = url.pathname;
        if (((_c = (_b = (_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.parentAsset._data) === null || _b === void 0 ? void 0 : _b.info.image) === null || _c === void 0 ? void 0 : _c.format) &&
            isAllowedFormat((_f = (_e = (_d = this.composeOptions) === null || _d === void 0 ? void 0 : _d.parentAsset._data) === null || _e === void 0 ? void 0 : _e.info.image) === null || _f === void 0 ? void 0 : _f.format)) {
            if (variantType !== 'original' &&
                pageNum !== undefined &&
                'numPages' in this.composeOptions.parentAsset._data.info.image &&
                this.composeOptions.parentAsset._data.info.image.numPages) {
                if (pageNum >= 1 && pageNum <= this.composeOptions.parentAsset._data.info.image.numPages) {
                    pathName += `/page_${pageNum}`;
                }
                else {
                    throw new Error(`page number must be in range: [1-${this.composeOptions.parentAsset._data.info.image.numPages}]`);
                }
            }
        }
        if (variantType !== 'original') {
            pathName += `/${variantType}`;
        }
        url.pathname = pathName;
        const pathUrl = `${url.toString()}&showDeleted=${showDeleted}`;
        return pathUrl;
    }
    // TODO: instead of getting id from link, lets get it from the Asset object. [https://gitlab.com/Cimpress-Technology/FileReview/sherbert/sdk/assets-sdk/-/issues/266]
    getUrlInternal() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
        let url = DEFAULT_SHERBERT_BASE_URL;
        if ((_b = (_a = this.composeOptions) === null || _a === void 0 ? void 0 : _a.parentAsset._data) === null || _b === void 0 ? void 0 : _b.properties.url) {
            url = (_d = (_c = this.composeOptions) === null || _c === void 0 ? void 0 : _c.parentAsset._data) === null || _d === void 0 ? void 0 : _d.properties.url;
        }
        else if (((_h = (_g = (_f = (_e = this.composeOptions) === null || _e === void 0 ? void 0 : _e.parentAsset._data) === null || _f === void 0 ? void 0 : _f._links) === null || _g === void 0 ? void 0 : _g.self) === null || _h === void 0 ? void 0 : _h.href) !== undefined &&
            ((_m = (_l = (_k = (_j = this.composeOptions) === null || _j === void 0 ? void 0 : _j.parentAsset._data) === null || _k === void 0 ? void 0 : _k._links) === null || _l === void 0 ? void 0 : _l.self) === null || _m === void 0 ? void 0 : _m.href) !== null) {
            // TODO: below logic should be corrected with the above ticket
            if (((_r = (_q = (_p = (_o = this.composeOptions) === null || _o === void 0 ? void 0 : _o.parentAsset._data) === null || _p === void 0 ? void 0 : _p._links) === null || _q === void 0 ? void 0 : _q.self) === null || _r === void 0 ? void 0 : _r.href.toLowerCase().startsWith(`${DEFAULT_SHERBERT_BASE_URL}/v2/assets/`)) ||
                ((_v = (_u = (_t = (_s = this.composeOptions) === null || _s === void 0 ? void 0 : _s.parentAsset._data) === null || _t === void 0 ? void 0 : _t._links) === null || _u === void 0 ? void 0 : _u.self) === null || _v === void 0 ? void 0 : _v.href.toLowerCase().startsWith(`${DEFAULT_SHERBERT_BASE_URL}/v3/assets/`))) {
                const assetId = fetchAssetIdFromSherbertUrl((_z = (_y = (_x = (_w = this.composeOptions) === null || _w === void 0 ? void 0 : _w.parentAsset._data) === null || _x === void 0 ? void 0 : _x._links) === null || _y === void 0 ? void 0 : _y.self) === null || _z === void 0 ? void 0 : _z.href);
                return `${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${assetId}`;
            }
            url = (_3 = (_2 = (_1 = (_0 = this.composeOptions) === null || _0 === void 0 ? void 0 : _0.parentAsset._data) === null || _1 === void 0 ? void 0 : _1._links) === null || _2 === void 0 ? void 0 : _2.self) === null || _3 === void 0 ? void 0 : _3.href;
        }
        if (url.includes('/metadata')) {
            url = url.replace('/metadata', '');
        }
        return url;
    }
    /**
     * Returns an unsigned URL of the asset or undefined if the asset is not prepared yet.
     * Using this unsigned URL will require additional authorization.
     */
    get unsignedUrl() {
        if (this.isVariant()) {
            const { parentAsset, processTypeUrlPath } = this.composeOptions;
            const parentUrl = parentAsset.unsignedUrl;
            return parentUrl ? `${parentUrl}/${processTypeUrlPath}` : undefined;
        }
        if (!this.hasAssetId()) {
            return undefined;
        }
        return `${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${this.data.id}`;
    }
    /**
     * Returns a URL of the asset or undefined if the asset is not signed yet.
     */
    get signedUrl() {
        if (!this.isSigned()) {
            return undefined;
        }
        const url = new URL(this.unsignedUrl);
        url.searchParams.set('signature', this.signature);
        return url.toString();
    }
    /**
     * Creates an `Asset` instance with a `File` blob as the data source.
     */
    static fromFile(options) {
        return new Asset(options);
    }
    /**
     * Creates an `Asset` instance with a resource URL as the data source.
     */
    static fromUri(options) {
        return new Asset(options);
    }
    /**
     * Creates an `Asset` instance with Sherbert asset metadata as the data source.
     */
    static fromData(options) {
        return new Asset(options);
    }
    /**
     * Creates an `Asset` instance with Sherbert asset documentItem data as the data source.
     */
    static fromDocumentItem(options) {
        return new Asset(options);
    }
    /**
     * Prepares the asset for use by creating or retrieving asset data.
     *
     * You can think of this operation as a synchronization between the client and server.
     * For example, calling `Asset.fromFile({ ... })` will create an `Asset` instance,
     * but calling `prepare` on that instance is what will actually make calls to the Sherbert API.
     *
     * If you're using `AssetStore.upload()`, this method will be automatically called for you.
     *
     * When `prepare` is called on an asset variant,
     * the variant will be automatically generated in Sherbert if it doesn't exist.
     *
     * The `prepare` operation is cached internally - any subsequent call to this method
     * will result in the initial Promise being returned.
     *
     * An optional `signal` parameter is supported to allow cancelling the operation on the client side.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `Preparation of a variant asset is not supported yet` if the asset is a variant.
     * @throws `Error` with message `AssetErrorMessage.InvalidSourceData` if the asset is not of type `AssetFromFileOptions` or `AssetFromUriOptions`.
     * @throws `Error` with message `AssetErrorMessage.PrepareDeletingAsset` if deleting is in progress.
     * @throws `Error` with message `AssetErrorMessage.PrepareDeletedAsset` if the asset is not a base assets.
     */
    prepare(params = {}, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status.type === AssetStatusType.Deleting) {
                throw new Error(AssetErrorMessage.PrepareDeletingAsset);
            }
            // It's possible to re-generate a deleted variant on-demand or prepare a hidden base asset,
            // so only throw for deleted base assets
            if (this.status.type === AssetStatusType.Deleted && !this.status.isHidden && !this.isVariant()) {
                throw new Error(AssetErrorMessage.PrepareDeletedAsset);
            }
            // If asset using signed url fails throw an error
            if (this.status.type === AssetStatusType.SignedUrlError) {
                throw new Error(AssetErrorMessage.AssetUploadSignedUrl);
            }
            if (!this.preparePromise ||
                this.status.type === AssetStatusType.Unprepared ||
                this.status.type === AssetStatusType.Error) {
                this.preparePromise = this._prepare(params, callback);
            }
            return this.preparePromise;
        });
    }
    setPlaceHolderAsset(assetData) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.setStatus({ type: AssetStatusType.Prepared });
            if (assetData._data) {
                this.setAssetData(Object.assign(Object.assign({}, assetData._data), { info: Object.assign({}, (_a = assetData._data) === null || _a === void 0 ? void 0 : _a.info), isLocallyProcessed: true }));
            }
        });
    }
    /**
     * Sets the signature of the base asset. In case the base asset isn't prepared yet, it will also prepare it.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message if the asset could not be signed.
     * @throws `Error` with message if the signature is not included in the response.
     *
     * @param params
     */
    presign({ signal, route } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.signature ||
                this.status.type === AssetStatusType.Deleted ||
                this.status.type === AssetStatusType.Deleting) {
                return;
            }
            if (this.isVariant()) {
                yield this.baseAsset.presign({ route });
                return;
            }
            // if no id, prepare first
            if (!this.isPrepared()) {
                yield this.prepare({ route });
            }
            // In case prepare didn't set the asset data id, throw error
            if (!this.isPrepared()) {
                throw new Error(AssetErrorMessage.MissingAssetId);
            }
            let result;
            try {
                const headers = yield this.getAuthHeaders();
                // By default, the signature returned here never expires.
                result = (yield presignAsset({ path: { id: this.data.id } }, { signal, headers }));
            }
            catch (error) {
                if (isAbortError(error)) {
                    return;
                }
                throw new Error(error);
            }
            if ((result === null || result === void 0 ? void 0 : result.status) === 200) {
                const { signature } = result.data;
                this.setSignature(signature);
                if (!signature) {
                    throw new Error(AssetErrorMessage.EmptySignature);
                }
            }
        });
    }
    /**
     * Deletes the current asset from the asset store by hiding it in sherbert by default.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `AssetErrorMessage.MissingAssetId` if the asset ID is missing.
     *
     * @param params
     * @param params.hideOnly - Decides whether to hide or delete an asset. If set to `false`, the asset will be deleted from Sherbert.
     * Be aware that deleting an asset might break documents where this asset is being used. Defaults to `true`.
     */
    delete({ hideOnly = true } = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status.type === AssetStatusType.Deleting) {
                yield when(() => this.status.type !== AssetStatusType.Deleting);
                return;
            }
            // if the asset is preparing we can't be sure if the asset was created or not on the server,
            // so we have to wait for the asset (and its id) to be created before proceeding to delete it
            if (this.status.type === AssetStatusType.Preparing) {
                this.setStatus({ type: AssetStatusType.Deleting, isHidden: hideOnly });
                yield when(() => { var _a; return ((_a = this.data) === null || _a === void 0 ? void 0 : _a.id) !== undefined; });
            }
            if (
            // Cannot check for `isPrepared` here, because `prepare` won't set the status to `Prepared` when status is `Deleting`.
            this.hasAssetId() &&
                (this.status.type !== AssetStatusType.Deleted || this.isHidden()) &&
                this.status.type !== AssetStatusType.Unprepared) {
                try {
                    this.setStatus({ type: AssetStatusType.Deleting, isHidden: hideOnly });
                    const headers = yield this.getAuthHeaders();
                    if (hideOnly) {
                        yield hideAsset({ path: { id: this.data.id } }, { headers });
                    }
                    else {
                        yield deleteAsset({ path: { id: this.data.id } }, { headers });
                    }
                    // Allow re-generation of variants
                    if (this.isVariant()) {
                        this.preparePromise = undefined;
                    }
                }
                catch (error) {
                    this.setErrorStatus(error);
                    return;
                }
            }
            this.setStatus({ type: AssetStatusType.Deleted, isHidden: hideOnly });
        });
    }
    /**
     * Patches the data of the current asset and sets the `isPatching` status metadata accordingly.
     *
     * ------------------------------------------------------------------------------------------------
     *
     * @throws `Error` with message `AssetErrorMessage.PatchUnpreparedAsset` if the asset is not prepared.
     * @throws `Error` with message if an error occurred while patching.
     *
     * @param params
     */
    patchData(patchData) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isPrepared()) {
                throw new Error(AssetErrorMessage.PatchUnpreparedAsset);
            }
            try {
                this.setStatus({ type: AssetStatusType.Prepared, isPatching: true });
                const { data: updatedData } = yield patchAsset({ path: { id: this.data.id }, body: patchData }, { headers: yield this.getAuthHeaders() });
                this.setAssetData(updatedData);
                this.setStatus({ type: AssetStatusType.Prepared, isPatching: false });
            }
            catch (error) {
                throw new Error(error);
            }
        });
    }
    /**
     * Checks if both the base asset's signature and data id are set.
     *
     * @returns a type predicate that guarantees that `signedUrl`, `unsignedUrl` and `signature` are set.
     */
    isSigned() {
        var _a;
        return !!((_a = this.baseAsset.data) === null || _a === void 0 ? void 0 : _a.id) && !!this.signature;
    }
    /**
     * Checks if the asset has an id and if the status is `Prepared`.
     *
     * @returns a type predicate that guarantees that `unsignedUrl` and `data.id` are set.
     */
    isPrepared() {
        return this.status.type === AssetStatusType.Prepared && this.hasAssetId();
    }
    /** The top-most non-variant asset */
    get baseAsset() {
        if (this.isVariant()) {
            return this.composeOptions.parentAsset.baseAsset;
        }
        return this;
    }
    get signature() {
        // Using _signature property here to avoid an infinite loop
        // eslint-disable-next-line no-underscore-dangle
        return this.baseAsset._signature;
    }
    /**
     * DO NOT CALL THIS METHOD DIRECTLY! Use `this.prepare` instead.
     */
    _prepare(_a = {}, callback) {
        var _b, _c, _d, _e, _f, _g, _h;
        var { signal, signedUrl, route } = _a, params = __rest(_a, ["signal", "signedUrl", "route"]);
        return __awaiter(this, void 0, void 0, function* () {
            if (this.status.type === AssetStatusType.Prepared) {
                return;
            }
            const query = __rest(params, []);
            const uploadStartTime = new Date().getTime();
            try {
                const isHidden = this.isHidden();
                const isVariant = this.isVariant();
                // Hidden base assets should stay hidden
                if (!isHidden || isVariant) {
                    this.setStatus({ type: AssetStatusType.Preparing });
                }
                let isUploadedJsonFile;
                let isUploadedJsonUrl;
                if (this.composeOptions &&
                    isFileAsset(this.composeOptions.parentAsset.uploadOptions) &&
                    isJsonFile(this.composeOptions.parentAsset.uploadOptions.file)) {
                    isUploadedJsonFile = true;
                }
                if (this.composeOptions &&
                    isUriAsset(this.composeOptions.parentAsset.uploadOptions) &&
                    isJsonUrl(this.composeOptions.parentAsset.uploadOptions.uri)) {
                    isUploadedJsonUrl = true;
                }
                const headers = yield this.getAuthHeaders();
                if (isHidden && isVariant && ((_b = this.data) === null || _b === void 0 ? void 0 : _b.id)) {
                    yield unhideAsset({ path: { id: this.data.id } }, { headers });
                }
                else if (this.isVariant() &&
                    this.composeOptions &&
                    !isDocumentItem(this.composeOptions) &&
                    !isUploadedJsonFile &&
                    !isUploadedJsonUrl &&
                    ((_c = this.composeOptions.parentAsset._data) === null || _c === void 0 ? void 0 : _c.type) !== 'data') {
                    yield this.baseAsset.prepare({ signal, route });
                    // TODO(#139): add support for nested variants
                    if (route !== null && route !== undefined && !isRoute(route)) {
                        throw new Error(`${route} is not a valid route`);
                    }
                    let data;
                    switch (route || Route.V3) {
                        case Route.V2:
                            data = (yield getVariantMetadataWithPolling({
                                path: {
                                    id: this.baseAsset.data.id,
                                    variant: this.composeOptions.processTypeUrlPath,
                                },
                                queryParams: Object.assign({}, query),
                            }, { signal, headers })).data;
                            break;
                        case Route.V3:
                            data = (yield getVariantMetadataWithPollingV3({
                                path: {
                                    id: this.baseAsset.data.id,
                                    variant: this.composeOptions.processTypeUrlPath,
                                },
                                queryParams: Object.assign({}, query),
                            }, { signal, headers })).data;
                            break;
                        default:
                            throw new Error(`${route} is not a valid route`);
                    }
                    this.setAssetData(data);
                }
                else if (isFileAsset(this.uploadOptions)) {
                    const _j = this.uploadOptions, { onProgress: handleOnProgress, authProvider } = _j, requestOptions = __rest(_j, ["onProgress", "authProvider"]);
                    let onProgress;
                    if (isHidden && !isVariant) {
                        onProgress = handleOnProgress;
                    }
                    else {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        onProgress = (percentComplete) => {
                            this.setStatus({ type: AssetStatusType.Preparing, uploadProgress: percentComplete });
                            handleOnProgress === null || handleOnProgress === void 0 ? void 0 : handleOnProgress(percentComplete);
                        };
                    }
                    let imageInfo;
                    let imageInfoData;
                    let imagePreview;
                    let uploadCallbackResponse;
                    try {
                        const assetInfo = this.getClientSideAssetInfo({ file: this.uploadOptions.file });
                        if (assetInfo) {
                            imageInfo = assetInfo;
                        }
                        if (callback instanceof Function) {
                            if (shouldGenerateImagePreview(this.uploadOptions.file)) {
                                imagePreview = generateImagePreview(this.uploadOptions.file, headers);
                                imagePreview.then((response) => {
                                    if (response && response[0] && response[0].uploadId) {
                                        imageInfo.then((info) => {
                                            var _a, _b;
                                            imageInfoData = info;
                                            uploadCallbackResponse = {
                                                imageInfo: info,
                                                tempUploadData: response[0],
                                                tempUploadUrl: `${UPLOADS_BASE_URL}/${(_a = response[0]) === null || _a === void 0 ? void 0 : _a.uploadId}`,
                                                isError: false,
                                            };
                                            callback(uploadCallbackResponse);
                                            // If imageInfoData contains imageInfo classification data then dispatch the ImageInfoClientSuccess event.
                                            if (imageInfoData) {
                                                const assetImageInfo = {
                                                    imageInfo: imageInfoData,
                                                };
                                                (_b = this.assetStoreEventTarget) === null || _b === void 0 ? void 0 : _b.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoClientSuccess, {
                                                    detail: assetImageInfo,
                                                }));
                                            }
                                        });
                                    }
                                });
                            }
                        }
                    }
                    catch (exception) {
                        uploadCallbackResponse = {
                            isError: true,
                        };
                        (_d = this.assetStoreEventTarget) === null || _d === void 0 ? void 0 : _d.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoClientFailure, { detail: undefined }));
                        if (callback) {
                            callback(uploadCallbackResponse);
                        }
                    }
                    let assetData;
                    // upload asset using signed url
                    if (signedUrl) {
                        const queryParams = signedUrl.substring(signedUrl.indexOf('?') + 1);
                        try {
                            assetData = (yield createAssetByFileWithPolling({ body: Object.assign({}, requestOptions) }, { signal, onProgress, headers: undefined }, queryParams)).data;
                        }
                        catch (error) {
                            this.setStatus({ type: AssetStatusType.SignedUrlError, rawError: error.data });
                            (_e = this.assetStoreEventTarget) === null || _e === void 0 ? void 0 : _e.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadUsingSignedUrlFailure, { detail: error.data }));
                            if (callback) {
                                callback(error.data);
                            }
                        }
                    }
                    else {
                        switch (route || Route.V3) {
                            case Route.V2:
                                assetData = (yield createAssetByFileWithPolling({ body: Object.assign({}, requestOptions) }, { signal, onProgress, headers })).data;
                                break;
                            case Route.V3:
                                assetData = (yield createAssetByFileWithPollingV3({ body: Object.assign({}, requestOptions) }, { signal, onProgress, headers })).data;
                                break;
                            default:
                                throw new Error(`${route} is not a valid route`);
                        }
                    }
                    if (assetData) {
                        // TODO: remove polling [https://gitlab.com/Cimpress-Technology/FileReview/sherbert/sdk/assets-sdk/-/issues/244]
                        const interval = setInterval(() => {
                            clearInterval(interval);
                            // If imageInfoData is undefined then start polling till we get imageInfo from classification and dispatch the ImageInfoClientSuccess event (For PDF assets)
                            if (!imageInfoData) {
                                imageInfo === null || imageInfo === void 0 ? void 0 : imageInfo.then((info) => {
                                    var _a;
                                    const assetImageInfo = {
                                        id: assetData === null || assetData === void 0 ? void 0 : assetData.id,
                                        imageInfo: info,
                                    };
                                    if (info) {
                                        (_a = this.assetStoreEventTarget) === null || _a === void 0 ? void 0 : _a.dispatchEvent(assetStoreEvent(AssetStoreEvent.ImageInfoClientSuccess, {
                                            detail: assetImageInfo,
                                        }));
                                    }
                                    imageInfoData = info;
                                    if (assetData &&
                                        ((assetData === null || assetData === void 0 ? void 0 : assetData.isLocallyProcessed) === true ||
                                            (assetData === null || assetData === void 0 ? void 0 : assetData.isLocallyProcessed) === undefined)) {
                                        this.setAssetData(Object.assign(Object.assign({}, assetData), { info: Object.assign(Object.assign({}, assetData.info), { image: imageInfoData }), isLocallyProcessed: true }));
                                    }
                                });
                            }
                        });
                        const uploadEndTime = new Date().getTime();
                        const totalUploadTimeMs = uploadEndTime - uploadStartTime;
                        this.setAssetData(Object.assign(Object.assign({}, assetData), { info: Object.assign(Object.assign({}, assetData.info), { image: imageInfoData }), isLocallyProcessed: true, totalUploadTimeMs }));
                    }
                }
                else if (isUriAsset(this.uploadOptions)) {
                    const _k = this.uploadOptions, { authProvider } = _k, requestOptions = __rest(_k, ["authProvider"]);
                    let asset;
                    if (signedUrl) {
                        const queryParams = getQueryParams(signedUrl);
                        if (queryParams)
                            try {
                                asset = yield createAssetByUriWithPolling({
                                    body: Object.assign({}, requestOptions),
                                    queryParams: { signature: queryParams.signature, folderId: queryParams.folderId },
                                }, { signal });
                            }
                            catch (error) {
                                this.setStatus({ type: AssetStatusType.SignedUrlError, rawError: error.data });
                                (_f = this.assetStoreEventTarget) === null || _f === void 0 ? void 0 : _f.dispatchEvent(assetStoreEvent(AssetStoreEvent.UploadUsingSignedUrlFailure, { detail: error.data }));
                                if (callback) {
                                    callback(error.data);
                                }
                            }
                    }
                    else {
                        switch (route || Route.V3) {
                            case Route.V2:
                                asset = yield createAssetByUriWithPolling({ body: Object.assign({}, requestOptions) }, { signal, headers });
                                break;
                            case Route.V3:
                                asset = yield createAssetByUriWithPollingV3({ body: Object.assign({}, requestOptions) }, { signal, headers });
                                break;
                            default:
                                throw new Error(`${route} is not a valid route`);
                        }
                    }
                    const uploadUriEndTime = new Date().getTime();
                    const totalUriUploadTimeMs = uploadUriEndTime - uploadStartTime;
                    this.setAssetData(Object.assign(Object.assign({}, asset.data), { totalUploadTimeMs: totalUriUploadTimeMs }));
                }
                else if (this.uploadOptions && 'data' in this.uploadOptions) {
                    const requestOptions = __rest(this.uploadOptions, []);
                    try {
                        const asset = yield createAssetByDocumentItemWithPollingV3({ body: Object.assign({}, requestOptions) }, { signal, headers });
                        let infoData;
                        switch (route || Route.V3) {
                            case Route.V2:
                                infoData = (yield getDocumentItemInfoV2({ path: { id: asset.data.id } }, { headers, signal })).data;
                                break;
                            case Route.V3:
                                infoData = (yield getDocumentItemInfoV3({ path: { id: asset.data.id } }, { headers, signal })).data;
                                break;
                            default:
                                throw new Error(`${route} is not a valid route`);
                        }
                        if (infoData) {
                            (_g = this.assetStoreEventTarget) === null || _g === void 0 ? void 0 : _g.dispatchEvent(assetStoreEvent(AssetStoreEvent.DocumentItemInfoSuccess, {
                                detail: infoData,
                            }));
                        }
                        this.setAssetData(Object.assign(Object.assign({}, asset.data), { info: Object.assign(Object.assign({}, asset.data.info), { documentItem: Object.assign({}, infoData) }) }));
                    }
                    catch (error) {
                        (_h = this.assetStoreEventTarget) === null || _h === void 0 ? void 0 : _h.dispatchEvent(assetStoreEvent(AssetStoreEvent.DocumentItemInfoFailure, {
                            detail: error,
                        }));
                        this.setStatus({ type: AssetStatusType.Error, rawError: error.data });
                        if (callback) {
                            callback(error.data);
                        }
                    }
                }
                else {
                    throw new Error(AssetErrorMessage.InvalidSourceData);
                }
                // Do not set Prepared status if the asset is being deleted during prepare or if the base asset
                // was hidden before being prepared (asset should always stay hidden in that case)
                // Or upload asset using signed Url fails
                if (this.status.type !== AssetStatusType.Deleting &&
                    this.status.type !== AssetStatusType.SignedUrlError &&
                    (!isHidden || isVariant)) {
                    this.setStatus({ type: AssetStatusType.Prepared });
                }
            }
            catch (error) {
                if (this.status.type === AssetStatusType.Deleted) {
                    return;
                }
                if (isAbortError(error)) {
                    this.setStatus({ type: AssetStatusType.Unprepared });
                    return;
                }
                this.setErrorStatus(error);
            }
        });
    }
    /**
     * Get the Page info of the specified page
     * @param {GetImageInfoPerPageParams} pageNo denotes the page no of which metadata is required
     * @returns {Promise<ImageMetadata>} Returns metadata of the page specified
     */
    getPageImageInfo(_a) {
        var _b, _c, _d, _e, _f, _g, _h;
        var { pageNo } = _a, params = __rest(_a, ["pageNo"]);
        return __awaiter(this, void 0, void 0, function* () {
            const imageMetadata = this._pageImageInfo[pageNo];
            const query = __rest(params, []);
            if (imageMetadata !== undefined && imageMetadata !== null && Object.keys(imageMetadata).length !== 0) {
                return imageMetadata;
            }
            if (!((_b = this.data) === null || _b === void 0 ? void 0 : _b.id)) {
                throw new Error('asset id not found');
            }
            if (pageNo < 1) {
                throw new Error(`${pageNo} is not a valid page number`);
            }
            if (((_c = this.data.info.image) === null || _c === void 0 ? void 0 : _c.format) &&
                isAllowedFormat((_d = this.data.info.image) === null || _d === void 0 ? void 0 : _d.format) &&
                'numPages' in this.data.info.image &&
                ((_e = this.data.info.image) === null || _e === void 0 ? void 0 : _e.numPages) &&
                ((_f = this.data.info.image) === null || _f === void 0 ? void 0 : _f.numPages) < pageNo) {
                throw new Error(`page number must in range: [1-${(_g = this.data.info.image) === null || _g === void 0 ? void 0 : _g.numPages}]`);
            }
            const imageInfo = (yield getPageImageInfo({ path: { id: (_h = this.data) === null || _h === void 0 ? void 0 : _h.id, pageNum: pageNo }, queryParams: Object.assign({}, query) }, { headers: yield this.getAuthHeaders() }));
            this._pageImageInfo[pageNo] = imageInfo.data;
            return imageInfo.data;
        });
    }
    /**
     * Gets the URL for the stacked variant of the asset of the appropriate variant value.
     * @param {GetStackedVariantUrlParams} denotes the variant to create in sequence and wether to include signature.
     * @param {VariantParams} denotes parameters to crop the asset and to get monochrome variant with threshold percentage and hex color code.
     * @returns {string} Returns the requested asset URL.
     */
    getStackedVariantUrlV2({ variant1, variant2, variant3, variant4, variant5, includeSignature = false, showDeleted = false, pageNum, }, { croppingParams, monochromeColorParams } = {}) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            if (!variant1) {
                throw new Error('variants are not specified');
            }
            let urlPath = `${DEFAULT_SHERBERT_BASE_URL}/${Route.V3}/assets/${this.data.id}`;
            if (pageNum) {
                urlPath += `/page_${pageNum}`;
            }
            const { l, r, t, b } = croppingParams || {};
            const { threshold, color, inverted } = monochromeColorParams || {};
            let cropParams;
            [variant1, variant2, variant3, variant4, variant5].forEach((variant) => {
                if (!variant)
                    return; // skip if variant is not specified
                switch (variant) {
                    case 'cropped':
                        cropParams = this.getCroppingParams(l, r, t, b);
                        urlPath += `/${variant}${cropParams}`;
                        break;
                    case 'monochrome':
                        urlPath += `/${variant}${this.getMonochromeColorParams(threshold, color, inverted)}`;
                        break;
                    default:
                        urlPath += `/${variant}`;
                        break;
                }
            });
            const url = new URL(urlPath);
            if (includeSignature) {
                url === null || url === void 0 ? void 0 : url.searchParams.set('signature', this.signature ? this.signature : '');
            }
            if (showDeleted) {
                url === null || url === void 0 ? void 0 : url.searchParams.set('showDeleted', 'true');
            }
            if (url) {
                return url.toString();
            }
            throw new Error('cannot generate the url');
        });
    }
    /**
     * Gets the URL for the stacked variant of the asset of the appropriate variant value.
     * @deprecated Use getStackedVariantUrlV2 instead.
     * @param {GetStackedVariantUrlParams} denotes the variant to create in sequence and wether to include signature.
     * @param {GetParametrizedCroppedParams} denotes parameters to crop the asset. Pass null or undefined if not required.
     * @param {MonochromeColorOptionParams} denotes parameters to get monochrome variant with threshold percentage and hex color code. Pass null or undefined if not required.
     * @returns {string} Returns the requested asset URL.
     */
    getStackedVariantUrl({ variant1, variant2, variant3, variant4, variant5, includeSignature = false, showDeleted = false, pageNum, }, croppingParams, monochromeColorParams) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            if (!variant1) {
                throw new Error('variants are not specified');
            }
            let urlPath = `${DEFAULT_SHERBERT_BASE_URL}/${Route.V3}/assets/${this.data.id}`;
            if (pageNum) {
                urlPath += `/page_${pageNum}`;
            }
            const { l, r, t, b } = croppingParams || {};
            const { threshold, color, inverted } = monochromeColorParams || {};
            let cropParams;
            [variant1, variant2, variant3, variant4, variant5].forEach((variant) => {
                if (!variant)
                    return; // skip if variant is not specified
                switch (variant) {
                    case 'cropped':
                        cropParams = this.getCroppingParams(l, r, t, b);
                        urlPath += `/${variant}${cropParams}`;
                        break;
                    case 'monochrome':
                        urlPath += `/${variant}${this.getMonochromeColorParams(threshold, color, inverted)}`;
                        break;
                    default:
                        urlPath += `/${variant}`;
                        break;
                }
            });
            const url = new URL(urlPath);
            if (includeSignature) {
                url === null || url === void 0 ? void 0 : url.searchParams.set('signature', this.signature ? this.signature : '');
            }
            if (showDeleted) {
                url === null || url === void 0 ? void 0 : url.searchParams.set('showDeleted', 'true');
            }
            if (url) {
                return url.toString();
            }
            throw new Error('cannot generate the url');
        });
    }
    /**
     * Fetches the latest Asset Info data and update the Asset
     */
    updateAssetInfo() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: infoResponseSchema } = yield getAssetInfoV3({ path: { id: this.data.id } }, { headers: yield this.getAuthHeaders() });
            const updatedAssetData = Object.assign(Object.assign({}, this.data), { info: infoResponseSchema });
            this.setAssetData(updatedAssetData);
        });
    }
    /**
     * Fetches Image Classification data and updates the Asset
     */
    updateAssetClassificationData() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: classificationData } = (yield getImageClassificationInfoV3({ path: { id: this.data.id } }, { headers: yield this.getAuthHeaders() }));
            const { classifications } = classificationData;
            if (classifications) {
                const updatedAssetData = Object.assign(Object.assign({}, this.data), { classifications });
                this.setAssetData(updatedAssetData);
            }
        });
    }
    /**
     * Fetches Image Storage data and updates the Asset
     */
    updateAssetStorageData() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: storageInfo } = (yield getStorageInfoV3({ path: { id: this.data.id } }, { headers: yield this.getAuthHeaders() }));
            if (storageInfo) {
                const updatedAssetData = Object.assign(Object.assign({}, this.data), { info: Object.assign(Object.assign({}, this.data.info), { storage: storageInfo }) });
                this.setAssetData(updatedAssetData);
            }
        });
    }
    /**
     * Gets the extracted image info variant of the asset specified by id
     * @param {GetMultiPageImageInfoParams} params Object holding getImageInfoMultiPage parameters for this method call.
     */
    getMultiPageImageInfo(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let { pageNum } = param;
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            pageNum = `page_${pageNum}`;
            const { data: multiPageInfo } = (yield getImageInfoMultipageV3({ path: { id: this.data.id, pageNum }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return multiPageInfo;
        });
    }
    /**
     * Gets the extracted logo info of the asset specified by id
     * @param {GetExtractedLogoParams} params Object holding getExtractedLogo parameters for this method call.
     */
    getExtractedLogo(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: extractedLogoInfo } = (yield getExtractedLogoV3({ path: { id: this.data.id }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return extractedLogoInfo;
        });
    }
    /**
    /**
     * Gets the extracted logo url of the asset specified by id and extractedAssetIndex
     * @param {GetExtractedLogoParamsWithId} params Object holding getExtractedLogoWithId parameters for this method call.
     */
    getExtractedLogoWithId(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { showDeleted, throwError, includeSignature, index } = param;
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const pathUrl = `${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${this.data.id}/extractedLogos/${index}`;
            const url = new URL(pathUrl);
            if (includeSignature) {
                url.searchParams.set('signature', this.signature ? this.signature : '');
            }
            const booleanParams = { showDeleted, throwError };
            Object.entries(booleanParams).forEach(([key, value]) => {
                if (typeof value === 'boolean') {
                    url.searchParams.set(key, value.toString());
                }
            });
            return url.toString();
        });
    }
    /**
     * Gets the extracted logo url of the asset specified by id and itemNumber
     * @param {GetExtractedLogoParamsWithId} params Object holding getExtractedLogoWithId parameters for this method call.
     */
    getExtractedLogoWithItemNumber(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { showDeleted, includeSignature, throwError, itemNumber } = param;
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const pathUrl = `${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${this.data.id}/extractedLogos/itemAt_${itemNumber}`;
            const url = new URL(pathUrl);
            if (includeSignature) {
                url.searchParams.set('signature', this.signature ? this.signature : '');
            }
            const booleanParams = { showDeleted, throwError };
            Object.entries(booleanParams).forEach(([key, value]) => {
                if (typeof value === 'boolean') {
                    url.searchParams.set(key, value.toString());
                }
            });
            return url.toString();
        });
    }
    /**
     * Gets the  vector extracted logo info of the asset specified by id
     * @param {getVectorExtractedLogoParams} params Object holding getVectorExtractedLogo parameters for this method call.
     */
    getVectorExtractedLogo(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: VectorextractedLogoInfo } = (yield getVectorExtractedLogo({ path: { id: this.data.id }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return VectorextractedLogoInfo;
        });
    }
    /**
     * Gets the  vector extracted logo info of the asset specified by id and itemNumber
     * @param {getVectorExtractedLogoWithItemNumber} params Object holding getVectorExtractedLogoWithItemNumber parameters for this method call.
     */
    getVectorExtractedLogoWithItemNumber(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { showDeleted, throwError, itemNumber, includeSignature } = param;
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const pathUrl = `${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${this.data.id}/vectorExtractedLogos/itemAt_${itemNumber}`;
            const url = new URL(pathUrl);
            if (includeSignature) {
                url.searchParams.set('signature', this.signature ? this.signature : '');
            }
            const booleanParams = { showDeleted, throwError };
            Object.entries(booleanParams).forEach(([key, value]) => {
                if (typeof value === 'boolean') {
                    url.searchParams.set(key, value.toString());
                }
            });
            return url.toString();
        });
    }
    /**
     * Gets the  vector extracted logo info of the asset specified by id version
     * @param {getVectorExtractedLogoWithVersion} params Object holding getVectorExtractedLogoWithVersion parameters for this method call.
     */
    getVectorExtractedLogoWithVersion(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { version } = param;
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: VectorextractedLogoWithVersionInfo } = (yield getVectorExtractedLogoWithVersionV3({ path: { id: this.data.id, version }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return VectorextractedLogoWithVersionInfo;
        });
    }
    /**
     * Gets the  vector extracted logo urls of the asset specified by version and itemNumber
     * @param {VectorExtractedLogoVersionItemNumberParam} params Object holding getVectorExtractedLogoWithVersionAndItemNumber parameters for this method call.
     */
    getVectorExtractedLogoVersionAndItemNumber(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { version, itemNumber } = param;
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const VectorextractedLogoWithVersionAndItemNumberInformation = (yield getVectorExtractedLogoWithVersionAndItemNumberV3({ path: { id: this.data.id, version, itemNumber }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return VectorextractedLogoWithVersionAndItemNumberInformation;
        });
    }
    /**
     * Gets the colors info of the asset specified by id
     * @param {GetColorParams} params Object holding getColorsInfo parameters for this method call.
     */
    getColorInfo(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: colorInfo } = (yield getColorsInfoV3({ path: { id: this.data.id }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return colorInfo;
        });
    }
    /**
     * Gets the fonts info of the asset specified by id
     * @param {GetFontsParams} params Object holding getFonts parameters for this method call.
     */
    getFonts(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: fontData } = (yield getFontsV3({ path: { id: this.data.id }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return fontData;
        });
    }
    /**
     * Gets the image text of the asset specified by id
     * @param {GetImageTextParams} params Object holding getFonts parameters for this method call.
     */
    getImageText(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { data: text } = (yield getImageTextV3({ path: { id: this.data.id }, queryParams: param }, { headers: yield this.getAuthHeaders() }));
            return text;
        });
    }
    /**
     * Gets the image URL after cropping.
     * @param {GetParametrizedCroppedParams} params Object holding getParameterizedCropping parameters for this method call.
     */
    getParameterizedCropping(param) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id)) {
                throw new Error('asset id not found');
            }
            const { l, r, b, t, includeSignature = false, showDeleted = false, pageNum } = param;
            let pathUrl = `${DEFAULT_SHERBERT_BASE_URL}/v3/assets/${this.data.id}`;
            if (pageNum) {
                pathUrl += `/page_${pageNum}`;
            }
            const cropParams = this.getCroppingParams(l, r, t, b);
            pathUrl += `/cropped${cropParams}`;
            const url = new URL(pathUrl);
            if (includeSignature) {
                url.searchParams.set('signature', this.signature ? this.signature : '');
            }
            if (showDeleted) {
                url.searchParams.set('showDeleted', 'true');
            }
            return url.toString();
        });
    }
    /**
     * Returns cached or new `VariantAsset` associated with provided `processTypeUrlPath`.
     * `processTypeUrlPath` is also used in `unsignedUrl` and `signedUrl` for variant asset URL construction.
     */
    derive(processTypeUrlPath) {
        let variantAsset = this.variantCache.get(processTypeUrlPath);
        if (!variantAsset) {
            variantAsset = new Asset({
                parentAsset: this,
                processTypeUrlPath,
                authProvider: this.authProvider,
            });
            this.variantCache.set(processTypeUrlPath, variantAsset);
        }
        return variantAsset;
    }
    setAssetData(assetData) {
        this._data = assetData;
    }
    setStatus(status) {
        this.status = status;
        if ((status.type === AssetStatusType.Deleting || status.type === AssetStatusType.Deleted) && !status.isHidden) {
            this.variantCache.forEach((variant) => {
                variant.setStatus(status);
            });
        }
    }
    setErrorStatus(error) {
        if (error instanceof Error) {
            this.setStatus({ type: AssetStatusType.Error, rawError: error });
        }
        else if (typeof error === 'string') {
            this.setStatus({ type: AssetStatusType.Error, rawError: new Error(error) });
        }
        else {
            this.setStatus({ type: AssetStatusType.Error, rawError: new Error('Unknown error') });
        }
    }
    setSignature(signature) {
        this._signature = signature;
    }
    isVariant() {
        return !!this.composeOptions;
    }
    isHidden() {
        return this.status.type === AssetStatusType.Deleted && this.status.isHidden;
    }
    hasAssetId() {
        var _a;
        return !!((_a = this.data) === null || _a === void 0 ? void 0 : _a.id);
    }
    getAuthHeaders() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Headers(yield this.authProvider.getAuthHeaders());
        });
    }
}
__decorate([
    observable
], Asset.prototype, "status", void 0);
__decorate([
    observable
], Asset.prototype, "_data", void 0);
__decorate([
    observable
], Asset.prototype, "_signature", void 0);
__decorate([
    computed
], Asset.prototype, "data", null);
__decorate([
    computed
], Asset.prototype, "unsignedUrl", null);
__decorate([
    computed
], Asset.prototype, "signedUrl", null);
__decorate([
    computed
], Asset.prototype, "baseAsset", null);
__decorate([
    computed
], Asset.prototype, "signature", null);
__decorate([
    action.bound
], Asset.prototype, "setAssetData", null);
__decorate([
    action.bound
], Asset.prototype, "setStatus", null);
__decorate([
    action.bound
], Asset.prototype, "setErrorStatus", null);
__decorate([
    action.bound
], Asset.prototype, "setSignature", null);
//# sourceMappingURL=Asset.js.map